import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Player, GameState, MatchStats } from '../types';
import { updateLiveScores } from '../services/tournamentService';
import { buildLiveFeed } from '../services/turnLog';

export const useGameState = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [winnerName, setWinnerName] = useState<string | undefined>(undefined);
  const [isGameRestored, setIsGameRestored] = useState(false);
  const [gameState, setGameState] = useState<GameState>({
    totalMatches: 1,
    currentMatch: 1,
    matchHistory: [],
    isMatchBased: false,
  });
  const [showMatchStats, setShowMatchStats] = useState(false);
  const navigate = useNavigate();

  // Load the saved state from localStorage only on page refresh
  useEffect(() => {
    const savedGameState = localStorage.getItem('gameState');
  
    if (savedGameState) {
      const parsedGameState = JSON.parse(savedGameState);
      
      setPlayers(parsedGameState.players || []);
      setCurrentPlayerIndex(parsedGameState.currentPlayerIndex || 0);
      setGameWon(parsedGameState.gameWon || false);
      setGameState(parsedGameState.gameState || {
        totalMatches: 1,
        currentMatch: 1,
        matchHistory: [],
        isMatchBased: false,
      });
      setShowMatchStats(parsedGameState.showMatchStats || false);
  
      if (window.location.pathname === '/' && !parsedGameState.gameWon) {
        navigate('/game');
      }
      setIsGameRestored(true);
    }
  }, [navigate]);

  // Sync live scores + match wins to Firebase when in a tournament match.
  // The payload is built by buildLiveFeed so the whole contract lives (and is
  // tested) in one place. currentPlayerIndex is a dependency on purpose: `_cur`
  // — whose turn it is — has to follow the turn even on a render where the
  // players array itself did not change.
  useEffect(() => {
    const ctx = gameState.tournamentContext;
    if (!ctx || !players.length) return;
    updateLiveScores(ctx.tournamentId, ctx.matchId, buildLiveFeed(players, currentPlayerIndex)).catch(() => {});
  }, [players, currentPlayerIndex, gameState.tournamentContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save the game state to localStorage whenever it changes
  useEffect(() => {
    if (isGameRestored) {
      const fullGameState = {
        players,
        currentPlayerIndex,
        gameWon,
        gameState,
        showMatchStats,
      };
      localStorage.setItem('gameState', JSON.stringify(fullGameState));
    }
  }, [players, currentPlayerIndex, gameWon, gameState, showMatchStats, isGameRestored]);

  const clearGameState = () => {
    localStorage.removeItem('gameState');
    setIsGameRestored(false);
  };

  const saveGameStateToLocalStorage = () => {
    const fullGameState = {
      players,
      currentPlayerIndex,
      gameWon,
      gameState,
      showMatchStats,
    };
    localStorage.setItem('gameState', JSON.stringify(fullGameState));
  };

  const startNewMatch = (newOrder?: { id: string; name: string }[]) => {
    // Determine base order: if newOrder provided, sort accordingly
    let orderedPlayers: Player[] = players;
    if (newOrder && newOrder.length > 0) {
      const orderIndex = new Map(newOrder.map((o, idx) => [o.id, idx] as const));
      orderedPlayers = [...players].sort((a, b) => {
        const ai = orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER;
        const bi = orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER;
        return ai - bi;
      });
    }

    // Reset players for new match but preserve their all-time stats
    const resetPlayers = orderedPlayers.map((player: Player) => ({
      ...player,
      dice: player.dice.map((die: any) => ({ value: 0, isFrozen: false })),
      score: 0,
      history: [],
      hasWon: false,
      isFinalThrow: false,
      throws: 0,
      isCurrent: false,
    }));

    if (resetPlayers.length > 0) {
      resetPlayers[0].isCurrent = true; // Set first player as current
    }

    setPlayers(resetPlayers);
    setCurrentPlayerIndex(0);
    setGameWon(false);
    setShowMatchStats(false);

    // Update match state
    setGameState((prev: GameState) => ({
      ...prev,
      currentMatch: prev.currentMatch + 1,
    }));
  };

  const completeMatch = (matchStats: MatchStats) => {
    // Update match history
    setGameState((prev: GameState) => ({
      ...prev,
      matchHistory: [...prev.matchHistory, matchStats],
    }));

    // Update player all-time stats
    const updatedPlayers = players.map((player: Player) => {
      const playerMatchStats = matchStats.playerStats.find(ps => ps.playerId === player.id);
      if (playerMatchStats) {
        const isWinner = playerMatchStats.name === matchStats.winner;
        return {
          ...player,
          matchWins: (player.matchWins || 0) + (isWinner ? 1 : 0),
          totalMatches: (player.totalMatches || 0) + 1,
          allTimeStats: {
            totalScore: (player.allTimeStats?.totalScore || 0) + playerMatchStats.score,
            totalThrows: (player.allTimeStats?.totalThrows || 0) + playerMatchStats.throws,
            totalTriples: (player.allTimeStats?.totalTriples || 0) + playerMatchStats.triples,
            totalTripleTriples: (player.allTimeStats?.totalTripleTriples || 0) + playerMatchStats.tripleTriples,
            totalFinalThrows: (player.allTimeStats?.totalFinalThrows || 0) + playerMatchStats.finalThrows,
            totalFrozenDice: (player.allTimeStats?.totalFrozenDice || 0) + playerMatchStats.frozenDiceCount,
            totalAccuracy: (player.allTimeStats?.totalAccuracy || 0) + playerMatchStats.accuracy,
            gamesPlayed: (player.allTimeStats?.gamesPlayed || 0) + 1,
          },
        };
      }
      return player;
    });

    setPlayers(updatedPlayers);
    
    // --- EARLY END LOGIC FOR BEST OF SYSTEM ---
    // Calculate majority needed to win
    const majority = Math.floor(gameState.totalMatches / 2) + 1;
    const playerWins = updatedPlayers.map((player: Player) => player.matchWins || 0);
    const hasMajorityWinner = playerWins.some(wins => wins >= majority);

    if (hasMajorityWinner) {
      setGameWon(true); // End the series early if someone has won the majority
    } else if (gameState.currentMatch >= gameState.totalMatches) {
      setGameWon(true); // This will trigger the final winner screen
    } else {
      setShowMatchStats(true); // Show match stats screen
    }
  };

  const isAllMatchesComplete = () => {
    return gameState.currentMatch >= gameState.totalMatches;
  };

  const getFinalWinner = () => {
    if (!isAllMatchesComplete()) return null;
    
    // Find player with most match wins
    const playerWins = players.map((player: Player) => ({
      name: player.name,
      wins: player.matchWins || 0,
    }));
    
    const maxWins = Math.max(...playerWins.map((p: any) => p.wins));
    const winners = playerWins.filter((p: any) => p.wins === maxWins);
    
    // Return tie information or single winner
    if (winners.length === 1) {
      return winners[0].name;
    } else {
      // Handle tie case - return names of tied players
      return `TIE_${winners.map((w: any) => w.name).join('_')}`;
    }
  };

  const getTieInfo = () => {
    if (!isAllMatchesComplete()) return null;
    
    const playerWins = players.map((player: Player) => ({
      name: player.name,
      wins: player.matchWins || 0,
    }));
    
    const maxWins = Math.max(...playerWins.map((p: any) => p.wins));
    const winners = playerWins.filter((p: any) => p.wins === maxWins);
    
    return {
      isTied: winners.length > 1,
      winners: winners,
      maxWins: maxWins,
    };
  };

  return {
    players,
    setPlayers,
    currentPlayerIndex,
    setCurrentPlayerIndex,
    gameWon,
    setGameWon,
    winnerName,
    setWinnerName,
    isGameRestored,
    clearGameState,
    saveGameStateToLocalStorage,
    gameState,
    setGameState,
    showMatchStats,
    setShowMatchStats,
    startNewMatch,
    completeMatch,
    isAllMatchesComplete,
    getFinalWinner,
    getTieInfo,
  };
};